import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalCloseButton,
    ModalBody,
    Box,
    Flex,
    HStack,
    VStack,
    Text,
    Tag,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Icon,
    Tooltip,
} from '@chakra-ui/react';
import { TbArrowBackUp, TbInfoCircle } from 'react-icons/tb';
import { OrderedSection, SupplierOrderStatus } from '../hooks/useGetOrderDetail';
import { formatPrice, formatPercentage } from '../functions';
import PartType from '../components/PartType';
import PartNumber from '../components/PartNumber';
import SupplierPartNumber from '../components/order/SupplierPartNumber';
import SourceBadge from '../components/SourceBadge';
import { useIsOverflowing } from '../hooks/useIsOverflowing';
import { getOrderedPartRemainingQuantity } from '../utils/canMovePartsToInventory';

const getStatusColor = (status: SupplierOrderStatus) => {
    switch (status) {
        case 'confirmed': return 'purple';
        case 'ordered': return 'teal';
        case 'rejected': return 'gray';
        case 'cancelled': return 'red';
        default: return 'gray';
    }
};

const getStatusLabel = (status: SupplierOrderStatus) => {
    switch (status) {
        case 'confirmed': return 'Confirmed';
        case 'ordered': return 'Ordered';
        case 'rejected': return 'Rejected';
        case 'cancelled': return 'Cancelled';
        default: return status;
    }
};

interface SupplierOrderSnapshotModalProps {
    isOpen: boolean;
    onClose: () => void;
    section?: OrderedSection;
    vin: string;
    roNumber?: string;
    ymm?: string;
}

/** Read-only snapshot of a supplier order's parts, mirroring the Ordered tab section. */
const SupplierOrderSnapshotModal = ({ isOpen, onClose, section, vin, roNumber, ymm }: SupplierOrderSnapshotModalProps) => {
    const [bodyRef, bodyOverflows] = useIsOverflowing(isOpen);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent maxW="80vw" borderRadius="xl" overflow="hidden">
                <ModalCloseButton zIndex={2} />
                {/* Header — PartOptionsModal style (gray.50 + bottom border) */}
                <Box
                    pt={6}
                    pb={5}
                    px={6}
                    pr={12}
                    bg="gray.50"
                    borderBottomWidth={bodyOverflows ? '1px' : '0'}
                    borderBottomStyle="solid"
                    borderColor="gray.200"
                    borderTopRadius="xl"
                >
                    {section ? (
                        <VStack align="stretch" spacing={2}>
                            <HStack spacing={2} flexWrap="wrap">
                                <SourceBadge source={section.source ?? 'partstrader'} />
                                <Text fontSize="lg" fontWeight="bold" color="gray.800" noOfLines={1}>
                                    {section.title}
                                </Text>
                                {section.displayId && (
                                    <Text
                                        fontSize="xs" color="gray.500" fontWeight="medium" bg="white"
                                        border="1px solid" borderColor="gray.200" borderRadius="md" px={1.5} py={0.5}
                                    >
                                        {section.displayId}
                                    </Text>
                                )}
                                <Tag size="sm" colorScheme={getStatusColor(section.status)} variant="subtle" fontSize="xs">
                                    {getStatusLabel(section.status)}
                                </Tag>
                            </HStack>
                            <HStack spacing={3} color="gray.500" fontSize="sm" flexWrap="wrap">
                                {roNumber && <Text>RO {roNumber}</Text>}
                                {ymm && <><Text color="gray.300">|</Text><Text noOfLines={1}>{ymm}</Text></>}
                                {section.timePlaced && (
                                    <>
                                        <Text color="gray.300">|</Text>
                                        <Text>
                                            {new Date(section.timePlaced).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </Text>
                                    </>
                                )}
                            </HStack>
                        </VStack>
                    ) : (
                        <Text fontSize="lg" fontWeight="bold" color="gray.800">Supplier Order</Text>
                    )}
                </Box>

                <ModalBody ref={bodyRef} minH={0} px={6} py={5}>
                    {!section ? (
                        <Flex py={12} justify="center"><Text fontSize="sm" color="gray.500">No order details available.</Text></Flex>
                    ) : (
                        <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflowX="auto">
                            <Table variant="simple" size="sm" w="100%" minW="870px" sx={{ tableLayout: 'fixed' }}>
                                <Thead>
                                    <Tr bg="gray.50">
                                        <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} w="220px">PART NAME</Th>
                                        <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} w="150px">OE PART NUMBER</Th>
                                        <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} w="150px">SUPPLIER PART NUMBER</Th>
                                        <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} w="80px" isNumeric>QTY</Th>
                                        <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} w="120px">DELIVERY ETA</Th>
                                        <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} w="110px" isNumeric>LIST PRICE</Th>
                                        <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} w="110px" isNumeric>BUY PRICE</Th>
                                        <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} w="110px" isNumeric>MARGIN</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {section.parts.map((part) => {
                                        const inactive = part.cancelled || part.returned;
                                        const textColor = part.cancelled ? 'red.500' : part.returned ? 'blue.500' : 'gray.900';
                                        const strike = inactive ? 'line-through' : undefined;
                                        return (
                                            <Tr key={part.partId} opacity={inactive ? 0.6 : 1}>
                                                <Td py={3} px={4}>
                                                    <VStack align="flex-start" spacing={1}>
                                                        <HStack spacing={1.5}>
                                                            <Text fontSize="sm" fontWeight="medium" color={textColor} textDecoration={strike}>
                                                                {part.name}
                                                            </Text>
                                                            {part.returned && (
                                                                <Tooltip label="Returned" hasArrow placement="top">
                                                                    <Box display="inline-flex">
                                                                        <Icon as={TbArrowBackUp as React.ElementType} w={3.5} h={3.5} color="blue.400" />
                                                                    </Box>
                                                                </Tooltip>
                                                            )}
                                                        </HStack>
                                                        {part.type ? <PartType partType={part.type} /> : null}
                                                    </VStack>
                                                </Td>
                                                <Td py={3} px={4}>
                                                    <Box textDecoration={strike}>
                                                        <PartNumber partNumber={part.partNumber} correctedPartNumber={part.correctedPartNumber} vin={vin} />
                                                    </Box>
                                                </Td>
                                                <Td py={3} px={4}>
                                                    <SupplierPartNumber
                                                        partNumber={part.partNumber}
                                                        correctedPartNumber={part.correctedPartNumber}
                                                        supplierPartNumber={part.supplierPartNumber}
                                                        inactive={inactive}
                                                        textColor={textColor}
                                                    />
                                                </Td>
                                                <Td py={3} px={4} isNumeric>
                                                    {(() => {
                                                        const inventoried = Math.max(0, part.quantityInventoried ?? 0);
                                                        const remaining = getOrderedPartRemainingQuantity(part);
                                                        if (inventoried > 0) {
                                                            return (
                                                                <Flex align="center" justify="flex-end" gap={1}>
                                                                    <VStack align="flex-end" spacing={0}>
                                                                        <Text
                                                                            fontSize="sm"
                                                                            color="gray.500"
                                                                            textDecoration="line-through"
                                                                        >
                                                                            {part.quantity ?? '-'}
                                                                        </Text>
                                                                        <Text
                                                                            fontSize="sm"
                                                                            color={inactive ? textColor : 'orange.500'}
                                                                            fontWeight="medium"
                                                                            textDecoration={strike}
                                                                        >
                                                                            {remaining}
                                                                        </Text>
                                                                    </VStack>
                                                                    <Tooltip
                                                                        label={`${inventoried} parts inventoried`}
                                                                        placement="top"
                                                                        hasArrow
                                                                    >
                                                                        <Box as="span" display="inline-flex" flexShrink={0}>
                                                                            <Icon
                                                                                as={TbInfoCircle as React.ElementType}
                                                                                w={3.5}
                                                                                h={3.5}
                                                                                color="gray.400"
                                                                            />
                                                                        </Box>
                                                                    </Tooltip>
                                                                </Flex>
                                                            );
                                                        }
                                                        return (
                                                            <Text fontSize="sm" color={textColor} textDecoration={strike}>
                                                                {part.quantity ?? '-'}
                                                            </Text>
                                                        );
                                                    })()}
                                                </Td>
                                                <Td py={3} px={4}>
                                                    {part.correctedDelivery && part.correctedDelivery !== part.delivery ? (
                                                        <VStack align="flex-start" spacing={0}>
                                                            <Text fontSize="sm" color="gray.500" textDecoration="line-through">{part.delivery || '-'}</Text>
                                                            <Text fontSize="sm" color={inactive ? textColor : 'orange.500'} fontWeight="medium" textDecoration={strike}>{part.correctedDelivery}</Text>
                                                        </VStack>
                                                    ) : (
                                                        <Text fontSize="sm" color={textColor} textDecoration={strike}>{part.delivery || '-'}</Text>
                                                    )}
                                                </Td>
                                                <Td py={3} px={4} isNumeric>
                                                    {part.correctedListPrice && part.correctedListPrice !== part.listPrice ? (
                                                        <VStack align="flex-end" spacing={0}>
                                                            <Text fontSize="sm" color="gray.500" textDecoration="line-through">{part.listPrice ? formatPrice(part.listPrice) : '-'}</Text>
                                                            <Text fontSize="sm" color={inactive ? textColor : 'orange.500'} fontWeight="medium" textDecoration={strike}>{formatPrice(part.correctedListPrice)}</Text>
                                                        </VStack>
                                                    ) : (
                                                        <Text fontSize="sm" color={textColor} textDecoration={strike}>{part.listPrice ? formatPrice(part.listPrice) : '-'}</Text>
                                                    )}
                                                </Td>
                                                <Td py={3} px={4} isNumeric>
                                                    {part.correctedBuyPrice && part.correctedBuyPrice !== part.buyPrice ? (
                                                        <VStack align="flex-end" spacing={0}>
                                                            <Text fontSize="sm" color="gray.500" textDecoration="line-through">{part.buyPrice ? formatPrice(part.buyPrice) : '-'}</Text>
                                                            <Text fontSize="sm" color={inactive ? textColor : 'orange.500'} fontWeight="medium" textDecoration={strike}>{formatPrice(part.correctedBuyPrice)}</Text>
                                                        </VStack>
                                                    ) : (
                                                        <Text fontSize="sm" color={textColor} textDecoration={strike}>{part.buyPrice ? formatPrice(part.buyPrice) : '-'}</Text>
                                                    )}
                                                </Td>
                                                <Td py={3} px={4} isNumeric>
                                                    <Text fontSize="sm" color={textColor} textDecoration={strike}>
                                                        {typeof part.grossMargin === 'number' ? formatPercentage(part.grossMargin) : '-'}
                                                    </Text>
                                                </Td>
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </Box>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default SupplierOrderSnapshotModal;
